import {NEW_ID} from '../../../../common/constants/index.js'
import ButtonPrimary from '../../../../common/components/Button/ButtonPrimary.js'
import ButtonSecondary from '../../../../common/components/Button/ButtonSecondary.js'
import {
  formSelector,
  getState,
  onlyIfAutoForm,
  updateForm,
  useSelector,
} from '../../../../store.js'
import {setShipper} from '../../../../data/shippers.js'
import {showGlobalError} from '../../../GlobalErrorMessage.js'
import api from '../../../../common/api.js'
import {NEWGISTICS} from '../../../../common/constants/ShipperNames.js'
import {isPresent} from '../../../../common/utils.js'
import Archive from './Archive.js'
import Nickname from './Nickname.js'
import ShippingMethods from './ShippingMethods.js'
import {
  baseSaveParamsSelector,
  baseShipperFormErrorsSelector,
  baseShipperInitialFormSelector,
  goToShipperSettings,
} from '../ShippersFunctions.js'
import TextInput from '../../Common/TextInput.js'
import className from '../../../../common/className.js'

export function setUpShipperForm({shipper, formName}) {
  return {
    formName,
    initialForm: {
      ...initialFormSelector(getState(), {shipper}),
      errorsSelector,
    },
  }
}

export function errorsSelector(state, {formName}) {
  const errors = baseShipperFormErrorsSelector(state, {formName})
  const form = formSelector(state, {
    formName,
  })

  if (!isPresent(form.merchantID)) {
    errors.merchantID = 'Merchant ID is required'
    errors.preventSave = true
  }

  if (!isPresent(form.clientFacilityID)) {
    errors.clientFacilityID = 'Client Facility ID is required'
    errors.preventSave = true
  }

  if (!isPresent(form.ngsFacilityID)) {
    errors.ngsFacilityID = 'NGS Facility ID is required'
    errors.preventSave = true
  }

  return errors
}

export function initialFormSelector(state, {shipper}) {
  const vendorConfig = shipper?.vendor_config || {}

  return {
    ...baseShipperInitialFormSelector(state, {shipper, vendor: NEWGISTICS}),
    merchantID: vendorConfig.merchant_id || '',
    clientFacilityID: vendorConfig.client_facility_id || '',
    ngsFacilityID: vendorConfig.ngs_facility_id || '',
  }
}

export async function saveShipperNewgistics(formName) {
  const form = formSelector(getState(), {formName})
  let [shipperID, params] = baseSaveParamsSelector(getState(), {formName})

  const {merchantID, clientFacilityID, ngsFacilityID} = form

  params = {
    ...params,
    merchant_id: merchantID,
    client_facility_id: clientFacilityID,
    ngs_facility_id: ngsFacilityID,
  }

  updateForm(formName, {isSaving: true})

  try {
    const {json} =
      shipperID !== NEW_ID
        ? await api.put(`/shipper/${shipperID}/`, params)
        : await api.post('/shipper/', {...params, vendor: NEWGISTICS})

    goToShipperSettings()

    setShipper(json)
  } catch (err) {
    showGlobalError(
      {
        summary: 'There was an error while saving shipper.',
      },
      err,
    )

    updateForm(formName, {isSaving: false})
  }
}

function NewgisticsForm({formName, form}) {
  const {id, merchantID, clientFacilityID, ngsFacilityID, isSaving} = form
  const errors = useSelector((state) => form.errorsSelector(state, {formName}))

  return (
    <div className="settings-list-item-form-wrap clear-both">
      <div className="medium-6 columns">
        <fieldset>
          <ul className="form-list margin-bottom-0">
            {id !== NEW_ID && <Nickname formName={formName} />}
            <li
              className={className`form-list-item bigger margin-bottom-20 ${{'margin-top-20 divider--top': form.id !== NEW_ID}}`}
            >
              <TextInput
                label="Merchant ID"
                name="newgistics_merchant_id"
                value={merchantID}
                onChange={(event) =>
                  updateForm(formName, {
                    merchantID: event.target.value,
                    hasChanged_merchantID: true,
                  })
                }
                errorMessage={form.hasChanged_merchantID && errors.merchantID}
                required
              />
            </li>
            <li className="form-list-item bigger margin-bottom-20">
              <TextInput
                label="Client Facility ID"
                name="newgistics_client_facility_id"
                value={clientFacilityID}
                onChange={(event) =>
                  updateForm(formName, {
                    clientFacilityID: event.target.value,
                    hasChanged_clientFacilityID: true,
                  })
                }
                errorMessage={
                  form.hasChanged_clientFacilityID && errors.clientFacilityID
                }
                required
              />
            </li>
            <li className="form-list-item bigger margin-bottom-20">
              <TextInput
                label="NGS Facility ID"
                name="newgistics_ngs_facility_id"
                value={ngsFacilityID}
                onChange={(event) =>
                  updateForm(formName, {
                    ngsFacilityID: event.target.value,
                    hasChanged_ngsFacilityID: true,
                  })
                }
                errorMessage={
                  form.hasChanged_ngsFacilityID && errors.ngsFacilityID
                }
                required
              />
            </li>
            {id !== NEW_ID && <ShippingMethods formName={formName} />}
            <li className="list__item list__item--no-style">
              <ButtonPrimary
                onClick={() => saveShipperNewgistics(formName)}
                isDisabled={errors.preventSave}
                isLoading={isSaving}
              >
                {id !== NEW_ID ? 'Save' : 'Create'}
              </ButtonPrimary>
              <ButtonSecondary onClick={() => goToShipperSettings()}>
                Cancel
              </ButtonSecondary>
            </li>
          </ul>
        </fieldset>
      </div>
      <div className="medium-5 columns">
        <fieldset>
          <div className="alert margin-bottom-10 alert--standard">
            {id === NEW_ID ? (
              <dl className="list">
                <dt className="list__title fs-00 lh-md margin-bottom-5">
                  Need help setting up your PB Standard account?
                </dt>
                <dd className="list__item fs-n0 margin-bottom-0">
                  Please contact{' '}
                  <a href="mailto:support@ordoro.com">support@ordoro.com</a> to
                  have this special shipper enabled in your account.
                  <div>
                    <a
                      className="btn--link mid meta-ups-setup-help"
                      href="https://support.ordoro.com/how-do-i-set-up-newgistics-in-ordoro/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      More info →
                    </a>
                  </div>
                </dd>
              </dl>
            ) : (
              <Archive formName={formName} />
            )}
          </div>
        </fieldset>
      </div>
    </div>
  )
}

export default onlyIfAutoForm(NewgisticsForm, setUpShipperForm)
